import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Loader2, Send, Trash2, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { KvkkPublishDialog } from './KvkkPublishDialog';
import { KvkkVersionCompaniesDialog } from './KvkkVersionCompaniesDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const KINDS = {
  kvkk: { path: 'kvkk-template', title: 'KVKK Taahhütnamesi Şablonu', doc: 'taahhütname' },
  aydinlatma: { path: 'aydinlatma-template', title: 'Aydınlatma Yükümlülüğü Şablonu', doc: 'Aydınlatma Yükümlülüğü belgesi' },
};

const downloadVersion = async (v, path) => {
  try {
    const res = await axios.get(`${API}/settings/${path}/versions/${v.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    Object.assign(document.createElement('a'), { href: url, download: v.filename }).click();
    URL.revokeObjectURL(url);
  } catch { toast.error('İndirilemedi'); }
};

const VersionRow = ({ v, onPublish, onDeleteDraft, onCompanies, busy, path }) => (
  <li className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-2 ${v.status === 'draft' ? 'border-amber-300 bg-amber-50/60' : v.current ? 'border-emerald-200 bg-emerald-50/40' : ''}`} data-testid={`kvkk-template-version-${v.version}`}>
    <FileText className="h-5 w-5 text-blue-600 shrink-0 hidden sm:block" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-800">v{v.version}</span>
        {v.status === 'draft' && <Badge className="bg-amber-100 text-amber-800">Taslak</Badge>}
        {v.current && <Badge className="bg-emerald-100 text-emerald-700">Yayında</Badge>}
        <span className="text-sm text-slate-700 truncate">{v.filename}</span>
      </div>
      <p className="text-xs text-slate-500">
        {fmtSize(v.size)} · Yüklendi: {fmtDate(v.uploaded_at)} ({v.uploaded_by})
        {v.status === 'published' && ` · Yayın: ${fmtDate(v.published_at)}`}
      </p>
      {v.status === 'published' && v.requested_count > 0 && (
        <button type="button" onClick={() => onCompanies(v)} className={`mt-1 text-xs font-medium underline-offset-2 hover:underline ${v.uploaded_count < v.requested_count ? 'text-amber-700' : 'text-emerald-700'}`} data-testid={`kvkk-template-version-companies-${v.version}`}>
          {v.uploaded_count}/{v.requested_count} firma yükledi{v.uploaded_count < v.requested_count ? ' · Yüklemeyenleri gör' : ''}
        </button>
      )}
    </div>
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" onClick={() => downloadVersion(v, path)} data-testid={`kvkk-template-version-download-${v.version}`}><Download className="h-4 w-4" /></Button>
      {v.status === 'draft' && (
        <>
          <Button size="sm" variant="ghost" onClick={onDeleteDraft} disabled={busy} className="text-rose-600 hover:bg-rose-50" data-testid="kvkk-template-draft-delete"><Trash2 className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => onPublish(v)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kvkk-template-publish-btn"><Send className="h-4 w-4 mr-1" />Yayınla</Button>
        </>
      )}
    </div>
  </li>
);

export const KvkkTemplateCard = ({ kind = 'kvkk' }) => {
  const { path, title, doc } = KINDS[kind];
  const inputRef = useRef(null);
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [companiesOf, setCompaniesOf] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/settings/${path}/versions`).then(res => setVersions(res.data)).catch(() => setVersions([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(pdf|docx?)$/i.test(file.name)) { toast.error('Sadece Word (.doc, .docx) veya PDF dosyası yükleyebilirsiniz'); return; }
    if (file.size > MAX_SIZE) { toast.error("Dosya boyutu 10 MB'ı aşamaz"); return; }
    const body = new FormData();
    body.append('file', file);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/settings/${path}`, body);
      toast.success(`v${res.data.version} taslak olarak yüklendi. Firmalara göndermek için "Yayınla"ya basın.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi');
    } finally { setBusy(false); }
  };

  const deleteDraft = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/settings/${path}/draft`); toast.success('Taslak silindi'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silinemedi'); }
    finally { setBusy(false); }
  };

  const hasDraft = versions?.some(v => v.status === 'draft');
  const hasPublished = versions?.some(v => v.current);

  return (
    <Card className="border-slate-200" data-testid={`${kind}-template-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />{title}</CardTitle>
        <CardDescription>Yeni şablon önce taslak olarak yüklenir. "Yayınla" ile seçtiğiniz firma statülerindeki firmalardan imzalı yeni {doc} istenir; yükleyene kadar çalışma izni talebi oluşturamazlar.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} data-testid={`${kind}-template-input`} />
        {versions === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {versions && !hasPublished && !hasDraft && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="kvkk-template-empty">
            Henüz şablon yok. Yayınlanana kadar kayıt ekranında indirme butonu görünmez.
          </p>
        )}
        {versions?.length > 0 && (
          <ul className="space-y-2 max-h-96 overflow-y-auto" data-testid="kvkk-template-versions">
            {versions.map(v => <VersionRow key={v.id} v={v} path={path} busy={busy} onPublish={setPublishing} onDeleteDraft={deleteDraft} onCompanies={setCompaniesOf} />)}
          </ul>
        )}
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid={`${kind}-template-upload-btn`}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {hasDraft ? 'Taslağı Değiştir' : 'Yeni Sürüm Yükle (Taslak)'}
        </Button>
      </CardContent>
      <KvkkVersionCompaniesDialog path={path} version={companiesOf} onClose={() => setCompaniesOf(null)} onChanged={load} />
      <KvkkPublishDialog path={path} version={publishing} onClose={() => setPublishing(null)} onPublished={() => { setPublishing(null); load(); }} />
    </Card>
  );
};
